import * as Yup from 'yup';
import { get, isEmpty, map } from '../utils/lodash';
import {
  TITLE_REQUIRED_TYPES,
  AUTHOR_REQUIRED_TYPES,
  YEAR_REQUIRED_TYPES,
  MATERIAL_TYPE_REQUIRED_TYPES,
  LINK_REQUIRED_TYPES,
} from '../utils/evidenceTypes';

export const evidenceInitialValues = (values, isParent = false) => {
  const authorList = (get(values, 'author') || '')
    .split(';')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);

  let initialObj = {
    content: (get(values, 'content') || '').trim(),
    source: (get(values, 'source') || '').trim(),
    evidenceType: get(values, 'evidenceType') || '',
    title: get(values, 'title') || '',
    authors: isEmpty(authorList) ? [''] : authorList,
    year: get(values, 'year') || '',
    doi: get(values, 'doi') || '',
    link: get(values, 'link') || '',
    materialTypeId: get(values, 'materialTypeId') || '',
    visibleOnPlatform: get(values, 'visibleOnPlatform') || false,
  };

  let linkedObjectives = get(values, 'objective', []);
  if (!isEmpty(linkedObjectives)) {
    linkedObjectives = map(linkedObjectives, (item) => item.id);
  }

  if (!isParent) {
    initialObj = { ...initialObj, linkedObjectives };
  }

  return initialObj;
};

export const evidenceValidationSchema = (t, isParent = false) => {
  let validationObj = {
    content: Yup.string().required(t('formulary.validation.content_required')),
    source: Yup.string(),
    evidenceType: Yup.number().required(
      t('formulary.validation.evidence_type_required')
    ),
    title: Yup.string().when('evidenceType', {
      is: (val) => !isParent && TITLE_REQUIRED_TYPES.includes(Number(val)),
      then: (schema) =>
        schema.required(t('formulary.validation.title_required')),
    }),
    authors: Yup.array()
      .of(Yup.string())
      .when('evidenceType', {
        is: (val) => !isParent && AUTHOR_REQUIRED_TYPES.includes(Number(val)),
        then: (schema) =>
          schema.test(
            'has-author',
            'Author is required',
            (list) =>
              Array.isArray(list) &&
              list.some((name) => name && name.trim().length > 0)
          ),
      }),
    year: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .typeError('Year must be a number')
      .when('evidenceType', {
        is: (val) => !isParent && YEAR_REQUIRED_TYPES.includes(Number(val)),
        then: (schema) => schema.required('Year is required'),
      }),
    materialTypeId: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .when('evidenceType', {
        is: (val) =>
          !isParent && MATERIAL_TYPE_REQUIRED_TYPES.includes(Number(val)),
        then: (schema) =>
          schema.required(t('formulary.validation.material_type_required')),
      }),
    doi: Yup.string(),
    link: Yup.string()
      .matches(/^https?:\/\//i, {
        message: t('formulary.validation.link_must_start_with'),
        excludeEmptyString: true,
      })
      .when('evidenceType', {
        is: (val) => !isParent && LINK_REQUIRED_TYPES.includes(Number(val)),
        then: (schema) =>
          schema.required(t('formulary.validation.link_required')),
      }),
    visibleOnPlatform: Yup.boolean(),
  };

  if (!isParent) {
    validationObj = {
      ...validationObj,
      linkedObjectives: Yup.array()
        .required(t('formulary.validation.linked_objectives_required'))
        .min(1, t('formulary.validation.linked_objectives_required')),
    };
  }

  return Yup.object().shape(validationObj);
};
